import * as React from "react";
import axios from "axios";
import {toast} from "react-toastify";
import {EMAIL_ERROR_MESSAGES} from "./Constants";

export function isValidEmailAddress(str) {
    return !! str.match(/^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,10})+$/);
}

export function popupCenter(url, title, w, h) {
    let left = (window.screen.width/2)-(w/2);
    let top = (window.screen.height/2)-(h/2);
    return window.open(url, title, 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width='+w+', height='+h+', top='+top+', left='+left);
}

export function numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// The user has a membership in the active company AND its QuickBooks tokens still work. The server clears
// qb_connection_status on disconnect or when QuickBooks rejects the refresh token; has_quickbooks_company
// alone only means "connected at some point". Use this everywhere the UI depends on the connection.
export function isQuickBooksConnected(userSettings) {
    return !!userSettings && userSettings.has_quickbooks_company === true && userSettings.qb_connection_status === true;
}

// Never show error text from the server as-is (it can be technical, or come from a third party such as Paddle).
// Every failure shows this one message, with a link that opens the support chat (Crisp). The fixed toastId keeps
// repeated failures to a single toast.
export function showGenericErrorToast() {
    toast.error(
        <span>Something went wrong. Please try again, or{" "}
            <a href="javascript:void(0)" onClick={() => window.$crisp && window.$crisp.push(["do", "chat:open"])}>contact support</a>
            {" "}if it keeps happening.</span>,
        {toastId: "generic-error"});
}

// The app's own wording for the codes the server puts in ?error= (LoginError in qrify/qb_bp/utils.py - add a code in
// both places). The URL only ever carries a code: anything else gets the generic message, so a crafted link can't make
// the app show arbitrary text. "info" codes aren't errors (shown neutrally on the login page).
const LOGIN_ERROR_MESSAGES = {
    session_expired: "Your session has expired. Please sign in again.",
    access_disabled: "Your access to this app has been disabled by your company admin.",
    signin_failed: "We couldn't sign you in. Please try again.",
    connect_failed: "We couldn't connect to QuickBooks. Please try again.",
    company_not_verified: "We couldn't verify your QuickBooks company connection. Please try again.",
    select_company: "Please select a QuickBooks company to finish signing in.",
    email_unverified: "Intuit hasn't verified your email address yet, so we can't sign you in. To verify it:",
    identity_mismatch: "This email address belongs to a different Intuit account. Please sign in with the Intuit account you originally used.",
    logged_out: "You were logged out.",
};
const LOGIN_INFO_CODES = ["logged_out"];

// {code, text, isInfo} for the ?error= code in a location's query string, or null when there is none. code is only
// used to pick a known extra block (the email_unverified steps on the login page), never shown.
export function loginErrorFromLocation(location) {
    const code = new URLSearchParams(location.search).get("error");
    if (!code) {
        return null;
    }
    return {
        code: code,
        text: LOGIN_ERROR_MESSAGES[code] || "Something went wrong. Please try again.",
        isInfo: LOGIN_INFO_CODES.includes(code),
    };
}

// pdf.js options for every react-pdf <Document>. The bundled pdf.js (2.10) can be made to run JavaScript embedded in a
// malicious PDF (CVE-2024-4367); disabling isEvalSupported is the official mitigation until react-pdf can be upgraded.
// A module-level constant on purpose: react-pdf reloads the document whenever it gets a new options object.
export const SAFE_PDF_OPTIONS = {isEvalSupported: false};

// /subscriptions/change_plan answers 402 {error: "payment_declined"} when Paddle couldn't charge the card for the
// change (its error 147): nothing changed, and the admin needs to update the payment method, then try again.
export function isPaymentDeclined(err) {
    return !!(err && err.response && err.response.status === 402 && err.response.data && err.response.data.error === "payment_declined");
}

// Opens Paddle's own "update payment method" page for the company's subscription (update_url from /get_settings, given
// to admins only) as an overlay. Returns false when there's no URL to open.
export function openPaymentMethodUpdate(updateUrl, onUpdated) {
    if (!updateUrl || !window.Paddle || !window.Paddle.Checkout) {
        return false;
    }
    window.Paddle.Checkout.open({override: updateUrl, successCallback: () => onUpdated && onUpdated()});
    return true;
}

// Opens Paddle's checkout for a plan, for a company that isn't paying yet (Free). The server issues and signs the
// passthrough (which user and company the purchase belongs to); the webhook ignores one built by the browser, and the
// server refuses it (409) for a company that is already paying, which must switch plans in place instead. Used by the
// Usage & Plan page and PremiumFeatureModal. onSuccess(data) runs when Paddle reports the payment (data.product.id is
// the plan), onClose when the checkout is closed. The returned promise rejects if the checkout couldn't be started.
export function startPlanCheckout(planId, userSettings, onSuccess, onClose) {
    return axios.post(window.SERVER_URL + '/subscriptions/checkout_passthrough', {}, {headers: {"X-Requested-With": "XMLHttpRequest"}}).then((response) => {
        const passthroughData = {
            ...response.data.passthrough,
            "user_ip_address": window.USER_IP_ADDRESS,
            "client_side_passthrough_date": new Date(),
            "user_user_agent": navigator.userAgent,
        };
        window.Paddle.Checkout.open({
            product: planId,
            email: userSettings.email_address,
            user_id: userSettings.id,
            passthrough: JSON.stringify(passthroughData),
            successCallback: (data) => onSuccess(data),
            closeCallback: () => onClose(),
        });
    });
}

// The message for a checkout that couldn't be started (startPlanCheckout's promise rejected).
export function checkoutErrorMessage(err) {
    return err && err.response && err.response.status === 403
        ? "Only a company admin can change the plan."
        : "We couldn't start the checkout. Please check your connection and try again.";
}

// The server refused because the company's plan doesn't include the feature (403 premium_required, e.g. emailing a QR
// invoice on the Free plan): the page offers the paid plans (PremiumFeatureModal) instead of an error.
// The message to show when emailing a QR invoice failed: the server's reason when it gave one, otherwise generic.
export function emailSendErrorMessage(err) {
    const code = err && err.response && err.response.data && err.response.data.error;
    return EMAIL_ERROR_MESSAGES[code] || "An error has occurred while sending the email. Please try again.";
}

export function isPremiumRequired(err) {
    return !!(err && err.response && err.response.status === 403 && err.response.data && err.response.data.error === "premium_required");
}
